import { writable, derived, get } from 'svelte/store';

/**
 * Custom Toast Library for Livewire
 *
 * A lightweight, Svelte-based toast notification system with smooth animations
 * and position control. Replaces the old bulma-toast implementation.
 *
 * @example
 * ```typescript
 * import { toast } from './lib/toast.svelte.ts';
 *
 * // Basic usage
 * toast.info('Hello world');
 * toast.success('Operation completed');
 * toast.error('Something went wrong');
 * toast.warning('Please check your input');
 *
 * // With custom duration (milliseconds)
 * toast.info('This stays for 5 seconds', 5000);
 *
 * // With position
 * toast.success('Top center message', 3000, 'top-center');
 *
 * // With slide preference
 * toast.info('Slides horizontally', 3000, 'bottom-right', 'horizontal');
 * toast.warning('Slides vertically', 3000, 'bottom-right', 'vertical');
 *
 * // Advanced usage - all parameters
 * toast.add('Custom message', 'info', 4000, 'top-left', 'horizontal');
 * ```
 */

export interface Toast {
	id: number;
	message: string;
	type?: 'success' | 'error' | 'info' | 'warning' | 'ghost';
	duration?: number;
	position?: 'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-center' | 'bottom-right';
	slidePreference?: 'vertical' | 'horizontal';
	showClose?: boolean;
	showIcon?: boolean;
	centerText?: boolean;
}

function createToastStore() {
	const { subscribe, update } = writable<Toast[]>([]);
	let nextId = 0;

	/**
	 * Add a new toast notification
	 * @param message - The text to display in the toast
	 * @param type - Toast type: 'success' | 'error' | 'info' | 'warning' | 'ghost' (default: 'info')
	 * @param duration - Display duration in milliseconds, 0 = persistent (default: 3000)
	 * @param position - Where to show the toast (default: 'bottom-right')
	 * @param slidePreference - Animation direction: 'horizontal' | 'vertical' (default: 'horizontal')
	 * @param showClose - Show close button (default: true)
	 * @param showIcon - Show type icon (default: true)
	 * @param centerText - Center align text content (default: false)
	 * @returns The toast ID for manual removal
	 */
	function add(message: string, type: Toast['type'] = 'info', duration = 3000, position: Toast['position'] = 'bottom-right', slidePreference: Toast['slidePreference'] = 'horizontal', showClose: boolean = true, showIcon: boolean = true, centerText: boolean = false) {
		const id = nextId++;
		const toast: Toast = { id, message, type, duration, position, slidePreference, showClose, showIcon, centerText };

		update(toasts => [...toasts, toast]);

		if (duration > 0) {
			setTimeout(() => remove(id), duration);
		}

		return id;
	}

	/**
	 * Remove a specific toast by ID
	 * @param id - The toast ID to remove
	 */
	function remove(id: number) {
		update(toasts => toasts.filter(t => t.id !== id));
	}

	/**
	 * Remove all active toasts
	 */
	function clear() {
		update(() => []);
	}

	return {
		subscribe,
		add,
		remove,
		clear,

		/**
		 * Show a success toast (green, with checkmark feel)
		 * @param msg - Message to display
		 * @param duration - Duration in ms (optional, default: 3000)
		 * @param position - Position (optional, default: 'bottom-right')
		 * @param slidePreference - Animation direction (optional, default: 'horizontal')
		 * @param showClose - Show close button (optional, default: true)
		 * @param showIcon - Show type icon (optional, default: true)
		 * @param centerText - Center align text content (optional, default: false)
		 */
		success: (msg: string, duration?: number, position?: Toast['position'], slidePreference?: Toast['slidePreference'], showClose?: boolean, showIcon?: boolean, centerText?: boolean) =>
			add(msg, 'success', duration, position, slidePreference, showClose, showIcon, centerText),

		/**
		 * Show an error toast (red, for failures/errors)
		 * @param msg - Message to display
		 * @param duration - Duration in ms (optional, default: 3000)
		 * @param position - Position (optional, default: 'bottom-right')
		 * @param slidePreference - Animation direction (optional, default: 'horizontal')
		 * @param showClose - Show close button (optional, default: true)
		 * @param showIcon - Show type icon (optional, default: true)
		 * @param centerText - Center align text content (optional, default: false)
		 */
		error: (msg: string, duration?: number, position?: Toast['position'], slidePreference?: Toast['slidePreference'], showClose?: boolean, showIcon?: boolean, centerText?: boolean) =>
			add(msg, 'error', duration, position, slidePreference, showClose, showIcon, centerText),

		/**
		 * Show an info toast (livewire orange, for general information)
		 * @param msg - Message to display
		 * @param duration - Duration in ms (optional, default: 3000)
		 * @param position - Position (optional, default: 'bottom-right')
		 * @param slidePreference - Animation direction (optional, default: 'horizontal')
		 * @param showClose - Show close button (optional, default: true)
		 * @param showIcon - Show type icon (optional, default: true)
		 * @param centerText - Center align text content (optional, default: false)
		 */
		info: (msg: string, duration?: number, position?: Toast['position'], slidePreference?: Toast['slidePreference'], showClose?: boolean, showIcon?: boolean, centerText?: boolean) =>
			add(msg, 'info', duration, position, slidePreference, showClose, showIcon, centerText),

		/**
		 * Show a warning toast (yellow, for warnings/cautions)
		 * @param msg - Message to display
		 * @param duration - Duration in ms (optional, default: 3000)
		 * @param position - Position (optional, default: 'bottom-right')
		 * @param slidePreference - Animation direction (optional, default: 'horizontal')
		 * @param showClose - Show close button (optional, default: true)
		 * @param showIcon - Show type icon (optional, default: true)
		 * @param centerText - Center align text content (optional, default: false)
		 */
		warning: (msg: string, duration?: number, position?: Toast['position'], slidePreference?: Toast['slidePreference'], showClose?: boolean, showIcon?: boolean, centerText?: boolean) =>
			add(msg, 'warning', duration, position, slidePreference, showClose, showIcon, centerText),

		/**
		 * Show a ghost toast (livewire orange text only, no background or borders)
		 * @param msg - Message to display
		 * @param duration - Duration in ms (optional, default: 3000)
		 * @param position - Position (optional, default: 'bottom-right')
		 * @param slidePreference - Animation direction (optional, default: 'horizontal')
		 * @param showClose - Show close button (optional, default: true)
		 * @param showIcon - Show type icon (optional, default: true)
		 * @param centerText - Center align text content (optional, default: false)
		 */
		ghost: (msg: string, duration?: number, position?: Toast['position'], slidePreference?: Toast['slidePreference'], showClose?: boolean, showIcon?: boolean, centerText?: boolean) =>
			add(msg, 'ghost', duration, position, slidePreference, showClose, showIcon, centerText),
	};
}

export const toast = createToastStore();